import { Alert, Box, Button, Checkbox, Container, FormControlLabel, Typography } from "@mui/material";
import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../allauth/auth";
import { logout } from "../allauth/lib/allauth";
import ApiClient from "../config/ApiClient";

const AccountDelete: React.FC = () => {
    const me = useAuth()?.data?.user;
    const isLoggedIn = Boolean(me?.id);
    const [confirmed, setConfirmed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [alreadyPending, setAlreadyPending] = useState(false);
    const [done, setDone] = useState(false);

    if (done) {
        return <Navigate to="/" replace />;
    }

    async function handleDelete() {
        if (!isLoggedIn) return;
        setError(null);
        setAlreadyPending(false);
        setSubmitting(true);
        try {
            const res = await ApiClient.post<{ detail?: string; deactivated_membership_count?: number }>(
                "/api/account/delete/",
                {}
            );
            if (res.success) {
                await logout();
                setDone(true);
                return;
            }
            const message = res.error ?? "Unable to process your deletion request. Please try again.";
            if (message.toLowerCase().includes("already pending")) {
                setAlreadyPending(true);
            } else {
                setError(message);
            }
        } catch {
            setError("Unable to process your deletion request. Please try again later.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
                Delete Account
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                This process immediately deactivates all access to your data and account by you and anyone else.
                It will also being a full data deletion request which will be executed within 10 days.
            </Typography>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    What happens next
                </Typography>
                <Typography variant="body1" component="ul" sx={{ pl: 2.5, m: 0, color: "text.secondary" }}>
                    <li>Your organization memberships will be deactivated immediately</li>
                    <li>You will be signed out of the platform</li>
                    <li>We will process removal of your personal data within 10 days.</li>
                    <li>
                        Some records may be anonymized and retained where required by law (for example, billing or
                        compliance records)
                    </li>
                </Typography>
            </Box>

            {!isLoggedIn && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    You must be signed in to delete your account.
                </Alert>
            )}

            {alreadyPending && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    A deletion request is already pending for this account. If you need help, contact{" "}
                    support@strongmindstrongperformance.com.
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <FormControlLabel
                control={
                    <Checkbox
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        disabled={!isLoggedIn || submitting}
                        data-attr="account-delete_confirm-checkbox"
                    />
                }
                label="I understand this will deactivate my account across all organizations"
                sx={{ mb: 3, alignItems: "flex-start", opacity: isLoggedIn ? 1 : 0.6 }}
            />

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {isLoggedIn && (
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!confirmed || submitting}
                        onClick={handleDelete}
                        data-attr="account-delete_submit-button"
                    >
                        {submitting ? "Processing…" : "Delete account"}
                    </Button>
                )}
            </Box>
        </Container>
    );
};

export default AccountDelete;
